// src/components/Separation/ResignationModal.tsx

import React, { useState } from 'react';
import { toast } from "sonner";
import { ResignationRequest } from '../../types/separation';
import { actionResignation, withdrawResignation, earlyReleaseAction } from '../../api/separation';

interface ResignationModalProps {
  isOpen: boolean;
  onClose: () => void;
  resignation: ResignationRequest | null;
  modalType: 'action' | 'withdraw' | 'earlyRelease';
  userRole: string;
  onSuccess: () => void;
}

const ResignationModal: React.FC<ResignationModalProps> = ({
  isOpen,
  onClose,
  resignation,
  modalType,
  userRole,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [actualLastDay, setActualLastDay] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');

  if (!isOpen || !resignation) return null;

  const handleAction = async () => {
    setLoading(true);
    try {
      await actionResignation(resignation.id, {
        action: actionType.toUpperCase() as 'APPROVE' | 'REJECT',
        remarks,
        actual_last_day: actualLastDay || undefined,
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    setLoading(true);
    try {
      await withdrawResignation(resignation.id, { reason: withdrawReason });
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEarlyRelease = async () => {
    setLoading(true);
    try {
      await earlyReleaseAction(resignation.id, {
        approved: true,
        remarks,
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (modalType === 'action') {
      return (
        <>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  value="approve"
                  checked={actionType === 'approve'}
                  onChange={() => setActionType('approve')}
                  className="text-green-600"
                />
                <span className="text-green-600">Approve</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  value="reject"
                  checked={actionType === 'reject'}
                  onChange={() => setActionType('reject')}
                  className="text-red-600"
                />
                <span className="text-red-600">Reject</span>
              </label>
            </div>
          </div>

          {userRole === 'admin' && actionType === 'approve' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Actual Last Working Day</label>
              <input
                type="date"
                value={actualLastDay}
                onChange={(e) => setActualLastDay(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Add your remarks here..."
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleAction}
              disabled={loading}
              className={`flex-1 py-2 rounded-lg text-white ${
                actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {loading ? 'Processing...' : actionType === 'approve' ? 'Approve' : 'Reject'}
            </button>
            <button onClick={onClose} className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
              Cancel
            </button>
          </div>
        </>
      );
    }

    if (modalType === 'withdraw') {
      return (
        <>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Withdrawal</label>
            <textarea
              value={withdrawReason}
              onChange={(e) => setWithdrawReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Please provide reason for withdrawing..."
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleWithdraw}
              disabled={loading}
              className="flex-1 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              {loading ? 'Processing...' : 'Withdraw Resignation'}
            </button>
            <button onClick={onClose} className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
              Cancel
            </button>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Early Release Approval</label>
          <p className="text-sm text-gray-600 mb-3">Employee has requested early release.</p>
          <textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Add remarks..."
          />
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleEarlyRelease}
            disabled={loading}
            className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {loading ? 'Processing...' : 'Approve Early Release'}
          </button>
          <button onClick={onClose} className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
            Cancel
          </button>
        </div>
      </>
    );
  };

  const getTitle = () => {
    if (modalType === 'action') return `${actionType === 'approve' ? 'Approve' : 'Reject'} Resignation`;
    if (modalType === 'withdraw') return 'Withdraw Resignation';
    return 'Early Release Request';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h3 className="text-lg font-semibold mb-4">{getTitle()}</h3>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default ResignationModal;